import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Activity, LayoutDashboard, Monitor, CalendarDays, Wrench,
  ShoppingCart, FileText, Users, ClipboardList, LogOut, Settings, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/tableUtils";

const MENUS = {
  it: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/aset", label: "Daftar Aset", icon: Monitor, testid: "nav-aset" },
    { to: "/jadwal", label: "Jadwal Maintenance", icon: CalendarDays, testid: "nav-jadwal" },
    { to: "/pengadaan", label: "Rekomendasi", icon: ShoppingCart, testid: "nav-pengadaan" },
    { to: "/laporan", label: "Laporan", icon: FileText, testid: "nav-laporan" },
  ],
  pimpinan: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/laporan/perbaikan", label: "Laporan Perbaikan", icon: ClipboardList, testid: "nav-laporan-perbaikan" },
    { to: "/laporan/maintenance", label: "Laporan Maintenance", icon: Wrench, testid: "nav-laporan-maintenance" },
    { to: "/laporan/pengadaan", label: "Laporan Rekomendasi", icon: ShoppingCart, testid: "nav-laporan-pengadaan" },
    { to: "/laporan/aset", label: "Laporan Aset", icon: Monitor, testid: "nav-laporan-aset" },
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menu = MENUS[user.role] || [];
  const [pendingJadwal, setPendingJadwal] = useState(0);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    const fetchPending = () => api.get("/schedules/today-pending").then((r) => setPendingJadwal(r.data.count)).catch(() => {});
    fetchPending();
    const timer = setInterval(fetchPending, 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col z-30" data-testid="sidebar">
        <div className="flex items-center gap-3 px-5 h-16 border-b border-border">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg leading-none tracking-tight" data-testid="sidebar-brand">SIPAT</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Pemeliharaan Aset TI</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="px-3 pb-2 text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
            Menu {user.role === "it" ? "IT" : "Pimpinan"}
          </p>
          {menu.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              data-testid={m.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                }`
              }
            >
              <m.icon className="w-4 h-4" />
              <span className="flex-1">{m.label}</span>
              {m.to === "/jadwal" && pendingJadwal > 0 && (
                <span className="bg-destructive text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="jadwal-notif-badge">
                  {pendingJadwal}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="mb-3 min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="sidebar-username">{user.username}</p>
            <Badge variant="secondary" className="mt-1 uppercase text-[10px]" data-testid="sidebar-role">
              {user.role === "it" ? "Staff IT" : "Pimpinan"}
            </Badge>
          </div>
          <div className="flex items-center gap-2" data-testid="sidebar-footer">
            {user.role === "it" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="settings-button" title="Pengaturan">
                    <Settings className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" data-testid="settings-menu">
                  <DropdownMenuItem onClick={() => navigate("/users")} data-testid="settings-user-menu">
                    <Users className="w-4 h-4 mr-2" /> User
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/ruangan")} data-testid="settings-ruangan-menu">
                    <Building2 className="w-4 h-4 mr-2" /> Ruangan
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="outline" size="icon" onClick={() => setConfirmLogout(true)} data-testid="logout-button" title="Keluar">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <ConfirmDialog
            open={confirmLogout}
            onOpenChange={setConfirmLogout}
            onConfirm={() => { setConfirmLogout(false); logout(); }}
            title="Apakah Anda yakin?"
            description="Anda akan keluar dari aplikasi SIPAT dan kembali ke halaman login."
            confirmLabel="Ya, Keluar"
            testid="confirm-logout"
          />
        </div>
      </aside>
      <div className="pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 h-16 bg-card border-b border-border flex items-center justify-between px-8" data-testid="app-header">
          <div>
            <span className="font-heading font-bold text-xl tracking-tight text-primary" data-testid="header-brand">SIPAT</span>
            <span className="hidden md:inline text-sm text-muted-foreground ml-3">Sistem Informasi Pemeliharaan Aset Teknologi Informasi</span>
          </div>
          <span className="text-sm text-muted-foreground" data-testid="header-user">{user.username}</span>
        </header>
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
